// Marketing-CMS block payload schemas. motiflink §2.2, migration 0008.
//
// THE MODEL: a block stores LITERAL copy in `content` plus a shallow per-locale
// overlay in `translations`. The renderer resolves it in one line:
//
//   const c = { ...content, ...(translations[lang] ?? {}) }
//
// `content` holds Arabic (the default language). A missing English override
// falls through to Arabic rather than rendering empty — deliberate, and the
// opposite of the M2a rule for UI strings: on marketing a visible Arabic word
// beats an invisible element.
//
// Language-neutral values (hrefs, image URLs, variants, icons, characters,
// numbers) live ONLY in `content` and are stripped from overlays by
// `blockTranslationsByType` — duplicating a URL per locale is how you get an
// English page pointing at a stale Arabic image.
//
// Adding a block type means: (1) extend the DB enum via migration,
// (2) add a Zod schema here + register it in `blockDataByType`,
// (3) add an Astro renderer at apps/web/src/blocks/<type>.astro,
// (4) add a BLOCK_SCHEMAS entry in the admin field editor.

import { z } from 'zod';

// ─── Primitives ─────────────────────────────────────────────────────────────

// User-facing copy. Bounded so a paste-bomb can't wreck a layout; the limits
// are generous enough for real marketing sentences.
const line = z.string().trim().min(1).max(300);
const optLine = z.string().trim().max(300).optional().nullable();
const paragraph = z.string().trim().min(1).max(2000);
const optParagraph = z.string().trim().max(2000).optional().nullable();

// Hrefs can be: a hash anchor (#cta), a same-origin path (/about), an absolute
// URL, or a mailto:/tel: link (the contact page needs both). The renderer
// treats them all as opaque. Block hrefs may also contain the tokens `{app}`
// and `{locale}`, resolved in BlockRenderer — a hardcoded /ar/signup would
// send English visitors to the Arabic app.
//
// The allowlist is deliberate: an unconstrained string here would accept
// `javascript:` and every CTA is rendered straight into an href.
export const hrefRef = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(
    /^([#/]|https?:\/\/|mailto:|tel:|\{app\})/,
    'must start with /, #, {app}, http://, https://, mailto: or tel:',
  );

const ctaShape = z.object({
  label: line,
  href: hrefRef,
  variant: z.enum(['primary', 'secondary', 'ghost']).default('primary'),
});

// Character slugs are constrained — the Astro renderer maps these to
// asset paths; arbitrary strings would 404. Add a new character =
// add the slug here + ship the assets in apps/web/public/images/.
export const characterSlug = z.enum(['basil', 'ward']);

// ─── 1. hero_split ─────────────────────────────────────────────────────────

export const heroSplitData = z.object({
  eyebrow: optLine,
  title: line,
  subtitle: optParagraph,
  ctas: z.array(ctaShape).min(1).max(3),
  // Optional flanking characters (one start, one end). Each is its own
  // optional slot so a page can use 0 / 1 / 2 characters.
  start_character: characterSlug.optional().nullable(),
  end_character: characterSlug.optional().nullable(),
});
export type HeroSplitData = z.infer<typeof heroSplitData>;

// ─── 2. stats_trio ─────────────────────────────────────────────────────────

const statItem = z.object({
  // The big number (or short string like "12"). Editors type this verbatim.
  value: z.string().trim().min(1).max(20),
  label: line,
});
export const statsTrioData = z.object({
  eyebrow: optLine,
  title: optLine,
  stats: z.tuple([statItem, statItem, statItem]),
});
export type StatsTrioData = z.infer<typeof statsTrioData>;

// ─── 3. dual_value_cards (Parents / Schools) ───────────────────────────────

const valueCard = z.object({
  eyebrow: optLine,
  title: line,
  body: paragraph,
  bullets: z.array(line).max(8).default([]),
  cta: ctaShape,
  // Cosmetic — drives the card's accent color in the renderer.
  accent: z.enum(['burgundy', 'sun', 'leaf', 'sky']).default('burgundy'),
});
export const dualValueCardsData = z.object({
  eyebrow: optLine,
  title: optLine,
  body: optParagraph,
  cards: z.tuple([valueCard, valueCard]),
});
export type DualValueCardsData = z.infer<typeof dualValueCardsData>;

// ─── 4. scrollable_grid_with_character ─────────────────────────────────────

const gridItem = z.object({
  glyph: z.string().trim().min(1).max(8), // e.g. "أ", "ب" — language-neutral
  label: optLine,
});
export const scrollableGridWithCharacterData = z.object({
  eyebrow: optLine,
  title: line,
  body: optParagraph,
  items: z.array(gridItem).min(4).max(40),
  character: characterSlug.optional().nullable(),
});
export type ScrollableGridWithCharacterData = z.infer<typeof scrollableGridWithCharacterData>;

// ─── 5. icon_card_grid ─────────────────────────────────────────────────────

const iconCard = z.object({
  // Icon slug constrained to the curated set bundled with apps/web.
  icon: z.enum([
    'sparkle',
    'book',
    'mosque',
    'globe',
    'leaf',
    'heart',
    'star',
    'sun',
    'compass',
    'puzzle',
  ]),
  title: line,
  body: paragraph,
});
export const iconCardGridData = z.object({
  eyebrow: optLine,
  title: line,
  body: optParagraph,
  cards: z.array(iconCard).min(2).max(6),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  // Section background — lets two adjacent icon grids alternate for rhythm.
  tone: z.enum(['cream', 'white']).default('cream'),
});
export type IconCardGridData = z.infer<typeof iconCardGridData>;

// ─── 6. pricing_tiers ──────────────────────────────────────────────────────

const pricingTier = z.object({
  name: line,
  tagline: optLine,
  // Display string (e.g. "39", "89"). Language-neutral digits; the renderer
  // pairs it with `currency` + `period`.
  price_display: z.string().trim().min(1).max(20),
  // Period suffix ("شهريًا" / "/mo") — localizable, so it lives in copy.
  //
  // OPTIONAL so a free tier can render its price_display alone ("مجاني" /
  // "Free"); with a period the renderer would emit "Free QAR per month".
  // Empty is the author's opt-in, rather than the renderer guessing that a
  // price_display of "0" means free — a 0 with a period is still a valid
  // promotional price.
  period: optLine,
  features: z.array(line).max(10).default([]),
  cta: ctaShape,
  highlighted: z.boolean().default(false),
  badge: optLine, // "Most popular" etc.
});
export const pricingTiersData = z.object({
  eyebrow: optLine,
  title: line,
  body: optParagraph,
  // Currency symbol/code — localizable (QAR ↔ ر.ق), so it's copy, not config.
  currency: line,
  tiers: z.array(pricingTier).min(1).max(4),
  schools_label: optLine,
  schools_cta: ctaShape.optional().nullable(),
});
export type PricingTiersData = z.infer<typeof pricingTiersData>;

// ─── 7. testimonial_pair ───────────────────────────────────────────────────

const testimonialItem = z.object({
  quote: paragraph,
  author: line,
  role: optLine,
  avatar_url: z.string().trim().max(512).optional().nullable(),
});
export const testimonialPairData = z.object({
  eyebrow: optLine,
  title: optLine,
  items: z.tuple([testimonialItem, testimonialItem]),
});
export type TestimonialPairData = z.infer<typeof testimonialPairData>;

// ─── 8. accordion_faq ──────────────────────────────────────────────────────

const faqItem = z.object({
  question: line,
  answer: paragraph,
});
export const accordionFaqData = z.object({
  eyebrow: optLine,
  title: line,
  body: optParagraph,
  items: z.array(faqItem).min(1).max(20),
});
export type AccordionFaqData = z.infer<typeof accordionFaqData>;

// ─── 9. footer_cta_newsletter ──────────────────────────────────────────────

export const footerCtaNewsletterData = z.object({
  eyebrow: optLine,
  title: line,
  body: optParagraph,
  // Primary CTA (e.g. "Start free trial"). Optional — some pages want
  // newsletter-only.
  cta: ctaShape.optional().nullable(),
  // Newsletter form copy. The form POSTs to /public/marketing/subscribe;
  // until that lands the endpoint 204s.
  newsletter: z
    .object({
      heading: line,
      placeholder: line,
      submit: line,
      success: line,
    })
    .optional()
    .nullable(),
});
export type FooterCtaNewsletterData = z.infer<typeof footerCtaNewsletterData>;

// ─── 10. rich_text (for About / Privacy / Terms) ───────────────────────────

export const richTextData = z.object({
  // Markdown, rendered to HTML by the Astro renderer. Long-form legal and
  // about copy lives here directly — that's the point of the overlay model.
  body: z.string().trim().min(1).max(50_000),
  eyebrow: optLine,
  title: optLine,
  // Maximum content width — admin chooses prose ("article") vs full.
  width: z.enum(['prose', 'wide', 'full']).default('prose'),
});
export type RichTextData = z.infer<typeof richTextData>;

// ─── contact_form ──────────────────────────────────────────────────────────
//
// The form on /contact. It carries ONLY its labels: the endpoint it posts to
// (POST /marketing/contact) and the `value` of each topic are code-owned, so
// editable copy can never redirect where a visitor's message goes, nor invent
// a topic the API's enum would reject.
//
// `value` is in NEUTRAL_KEYS, so an overlay may translate a topic's `label`
// while its `value` stays the single machine-readable string. That is exactly
// the split this block needs.

const contactTopicOption = z.object({
  /** Must match one of CONTACT_TOPICS in apps/api/src/routes/marketing.ts. */
  value: z.enum(['support', 'billing', 'schools', 'feedback', 'other']),
  label: line,
});

export const contactFormData = z.object({
  eyebrow: optLine,
  title: line,
  body: optParagraph,
  name_label: line,
  name_placeholder: optLine,
  email_label: line,
  email_placeholder: optLine,
  phone_label: line,
  phone_placeholder: optLine,
  topic_label: line,
  topic_options: z.array(contactTopicOption).min(1).max(8),
  message_label: line,
  message_placeholder: optLine,
  submit: line,
  /** Shown while the request is in flight. */
  sending: line,
  /** Shown after the API confirms the message was STORED, not merely emailed. */
  success: paragraph,
  error: paragraph,
});

export type ContactFormData = z.infer<typeof contactFormData>;

// ─── Dispatcher ────────────────────────────────────────────────────────────

// Exhaustive map: every block_type → its Zod schema. The API uses this
// in BOTH directions:
//   - Validate POST/PATCH bodies against the right schema for the block
//     type the caller is editing.
//   - Validate stored content on read, so a malformed row doesn't poison the
//     render path.

export const blockDataByType = {
  hero_split: heroSplitData,
  stats_trio: statsTrioData,
  dual_value_cards: dualValueCardsData,
  scrollable_grid_with_character: scrollableGridWithCharacterData,
  icon_card_grid: iconCardGridData,
  pricing_tiers: pricingTiersData,
  testimonial_pair: testimonialPairData,
  accordion_faq: accordionFaqData,
  footer_cta_newsletter: footerCtaNewsletterData,
  rich_text: richTextData,
  contact_form: contactFormData,
} as const;

export type BlockType = keyof typeof blockDataByType;
export const BLOCK_TYPES = Object.keys(blockDataByType) as BlockType[];

export type BlockDataByType = {
  [K in BlockType]: z.infer<(typeof blockDataByType)[K]>;
};

// ─── Translation overlays ──────────────────────────────────────────────────

// An overlay is the SAME shape as content, with every field optional — you
// translate the fields you translate, and the rest falls through to base.
//
// Built by walking each block schema and making it deep-partial, then stripping
// the language-neutral keys. Deriving it from the content schema (rather than
// hand-writing ten more schemas) means the two can never drift: add a field to
// hero_split and its overlay accepts it automatically.

// Keys whose values are language-neutral and must live only in `content`.
// An overlay carrying these is rejected — that's how an English page ends up
// pointing at a stale Arabic image or a broken link.
const NEUTRAL_KEYS = new Set([
  'href',
  'variant',
  'accent',
  'icon',
  'glyph',
  'columns',
  'tone',
  'width',
  'avatar_url',
  'image_url',
  'start_character',
  'end_character',
  'character',
  'highlighted',
  'value',
]);

// ⚠️ `price_display` is deliberately NOT neutral.
//
// It is neutral for a NUMERAL ("39" reads the same in both languages) but not
// for a free tier, whose price slot carries a WORD — "Free" / "مجاني". Listing
// it as neutral forced the Arabic word onto the English page. Numerals simply
// need no overlay, so leaving it translatable costs nothing and unblocks the
// one case that genuinely differs.
//
// The old whole-array override made this invisible: an overlay could not carry
// price_display, and replacing the array dropped it, so English prices
// rendered EMPTY on production. Per-item merging (resolveBlockContent) plus
// this line are two halves of the same fix — see blocks-overlay.test.ts.

/**
 * Recursively make a Zod schema partial and drop language-neutral keys, so the
 * result validates a translation overlay for the given content schema.
 *
 * Tuples keep their arity (a stats_trio overlay is still exactly three stats)
 * but every member becomes optional, which is what makes the parallel-array
 * overlay of motiflink §2.2 work: `translations.en.items[2].title`.
 */
function toOverlay(schema: z.ZodTypeAny): z.ZodTypeAny {
  // Zod 4 internals: `_def.type` is a string tag ('object' | 'array' | …).
  // (Zod 3's `_def.typeName` is gone — reading it silently returns undefined,
  // which would make every overlay an unvalidated passthrough.)
  const def = schema._def as unknown as {
    type: string;
    element?: z.ZodTypeAny;
    items?: z.ZodTypeAny[];
    innerType?: z.ZodTypeAny;
  };

  switch (def.type) {
    case 'object': {
      // `.shape` is Readonly in Zod 4, so build a fresh mutable shape rather
      // than mutating it in place.
      const shape = (schema as z.ZodObject<z.ZodRawShape>).shape as Record<string, z.ZodTypeAny>;
      const next: Record<string, z.ZodTypeAny> = {};
      for (const [key, value] of Object.entries(shape)) {
        if (NEUTRAL_KEYS.has(key)) continue;
        next[key] = toOverlay(value).optional();
      }
      // .strict() so an overlay carrying a neutral key (a translated href, a
      // per-locale image) is REJECTED rather than silently ignored.
      return z.strictObject(next);
    }
    case 'array':
      return z.array(toOverlay(def.element!));
    case 'tuple':
      return z.tuple((def.items ?? []).map((item) => toOverlay(item).optional()) as unknown as []);
    // Unwrap the modifier chain so `.optional().nullable().default()` doesn't
    // hide the object/array underneath.
    case 'optional':
    case 'nullable':
      return toOverlay(def.innerType!).nullable();
    case 'default':
      return toOverlay(def.innerType!);
    default:
      return schema;
  }
}

export const blockTranslationsByType = Object.fromEntries(
  Object.entries(blockDataByType).map(([type, schema]) => [type, toOverlay(schema)]),
) as { [K in BlockType]: z.ZodTypeAny };

/**
 * Validate a block's full translations map: `{ ar: {...}, en: {...} }`.
 * Unknown language codes are allowed (languages are DB-driven since M2a), but
 * each overlay must match the block type's shape.
 */
export function blockTranslationsSchema(type: BlockType) {
  return z.record(z.string().min(2).max(10), blockTranslationsByType[type]);
}

/**
 * Resolve a block for one language — the one line of motiflink §2.2.
 *
 * Shallow for scalars: `translations.en.title` overrides `content.title`
 * wholesale.
 *
 * 🔴 ARRAYS OF OBJECTS MERGE PER ITEM, and that is not a refinement — it is a
 * correctness fix for a bug that was LIVE on fajrarabi.com/en/pricing:
 *
 *   The English pricing page rendered NO PRICES AT ALL. Every tier's price slot
 *   was empty, so an English visitor could not see what anything cost.
 *
 * Why: a whole-array override means the overlay's tier objects REPLACE the base
 * ones, and the overlay legitimately omits `price_display` — prices are
 * language-neutral, and `toOverlay()`'s strictObject explicitly REJECTS
 * language-neutral keys in an overlay. So the field the overlay is forbidden to
 * carry was the field a whole-array override required it to carry. The two
 * rules contradicted each other, and copy silently vanished in between.
 *
 * Merging item-by-item resolves it: the overlay supplies the translated
 * strings, the base supplies everything language-neutral (price_display, href,
 * variant, highlighted, image_url…). An overlay array that is SHORTER than the
 * base keeps the untranslated tail items in the base language, which matches
 * the fall-through-to-Arabic rule the renderer already documents.
 *
 * Arrays of primitives (a feature list) still replace wholesale — there is no
 * per-item identity to merge on, and a translated list is authored as a unit.
 */
function mergeOverlayValue(base: unknown, over: unknown): unknown {
  if (Array.isArray(base) && Array.isArray(over)) {
    // An array of PRIMITIVES (a feature list) replaces as a unit: there is no
    // per-item identity to merge on, and a translated list is authored whole.
    // Keeping a base tail here would leak Arabic bullets into an English list
    // that deliberately has fewer of them.
    const mergesPerItem = over.some(isPlainObject) || base.some(isPlainObject);
    if (!mergesPerItem) return over;

    // Objects merge per index: the overlay carries the copy, the base keeps
    // every language-neutral field. A SHORTER overlay leaves the untranslated
    // tail in the base language, matching the fall-through-to-Arabic rule.
    // `Array.isArray` narrows `unknown` to `any[]`; re-annotate as `unknown[]`
    // so the element types stay checked.
    const overItems: unknown[] = over;
    const baseItems: unknown[] = base;
    return overItems
      .map((item, i) => {
        const baseItem = baseItems[i];
        return isPlainObject(item) && isPlainObject(baseItem)
          ? mergeOverlayObject(baseItem, item)
          : item;
      })
      .concat(baseItems.slice(overItems.length));
  }
  if (isPlainObject(base) && isPlainObject(over)) return mergeOverlayObject(base, over);
  return over;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Merge one overlay object onto its base, dropping blank overlay values. */
function mergeOverlayObject(
  base: Record<string, unknown>,
  over: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = k in base ? mergeOverlayValue(base[k], v) : v;
  }
  return out;
}

export function resolveBlockContent<T extends Record<string, unknown>>(
  content: T,
  translations: Record<string, Record<string, unknown>> | null | undefined,
  lang: string,
): T {
  const overlay = translations?.[lang];
  if (!overlay) return content;
  // Blank overlay values fall through to base rather than blanking the element.
  // patchField in packages/ui collapses emptied fields to null, so this is the
  // receiving half of that contract.
  return mergeOverlayObject(content, overlay) as T;
}

// ─── Admin I/O schemas (M4, motiflink §2.4) ────────────────────────────────
//
// `content` and `translations` are typed `unknown` at this layer on purpose:
// the right schema depends on the block's `type`, which the route knows only
// after it has parsed the envelope (or loaded the row). The route then runs
// blockDataByType[type] / blockTranslationsSchema(type) as a SECOND parse.
// Both halves are mandatory — an overlay carrying a language-neutral key
// (href, icon, image_url) must be rejected, which is what the strictObject in
// toOverlay() enforces.

export const blockTypeInput = z.enum(BLOCK_TYPES as [BlockType, ...BlockType[]]);

/** Page slugs may nest ("legal/privacy"); the homepage is the empty string. */
export const pageSlug = z
  .string()
  .trim()
  .max(80)
  .regex(/^$|^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/, 'lowercase slug segments');

/** Free-form slug for the non-page CMS entities (menus, static pages). */
export const entitySlug = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/, 'lowercase slug segments');

/**
 * Scalar per-locale overlay for the non-block entities (page meta, static
 * pages, menus, banners). Same §2.2 rule, but the fields are flat strings so
 * there is nothing type-dispatched to validate against.
 */
export const scalarTranslations = z.record(
  z.string().min(2).max(10),
  z.record(z.string().min(1).max(80), z.string().max(50_000).nullable()),
);

export const createPageInput = z.object({
  slug: pageSlug,
  title: z.string().trim().max(200).nullish(),
  isActive: z.boolean().default(true),
  isHomepage: z.boolean().default(false),
  metaTitle: z.string().trim().max(300).nullish(),
  metaDescription: z.string().trim().max(1000).nullish(),
  metaKeywords: z.string().trim().max(500).nullish(),
  ogImageMediaId: z.uuid().nullish(),
  localeScope: z.enum(['ar', 'en']).nullish(),
  translations: scalarTranslations.optional(),
});

export const updatePageInput = z
  .object({
    slug: pageSlug,
    title: z.string().trim().max(200).nullable(),
    isActive: z.boolean(),
    isHomepage: z.boolean(),
    metaTitle: z.string().trim().max(300).nullable(),
    metaDescription: z.string().trim().max(1000).nullable(),
    metaKeywords: z.string().trim().max(500).nullable(),
    ogImageMediaId: z.uuid().nullable(),
    localeScope: z.enum(['ar', 'en']).nullable(),
    translations: scalarTranslations,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const createBlockInput = z.object({
  type: blockTypeInput,
  title: z.string().trim().max(200).nullish(),
  // Optional so the admin "add widget" flow can POST an empty block and let
  // the field editor fill it in (§8). When absent the row starts at `{}`.
  content: z.unknown().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  translations: z.unknown().optional(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

export const updateBlockInput = z
  .object({
    title: z.string().trim().max(200).nullable(),
    content: z.unknown(),
    settings: z.record(z.string(), z.unknown()),
    translations: z.unknown(),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(9999),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

/** PATCH …/blocks/reorder — the admin sends the full reindexed 0..n-1 list. */
export const reorderBlocksInput = z.object({
  order: z
    .array(z.object({ id: z.uuid(), orderIndex: z.number().int().min(0).max(9999) }))
    .min(1)
    .max(200),
});

export const createSnapshotInput = z.object({
  label: z.string().trim().min(1).max(200).default('manual snapshot'),
});

// ─── Static pages ──────────────────────────────────────────────────────────

export const createStaticPageInput = z
  .object({
    title: z.string().trim().min(1).max(300),
    slug: entitySlug,
    content: z.string().max(200_000).nullish(),
    metaTitle: z.string().trim().max(300).nullish(),
    metaDescription: z.string().trim().max(1000).nullish(),
    metaKeywords: z.string().trim().max(500).nullish(),
    ogImageUrl: z.string().trim().max(512).nullish(),
    isActive: z.boolean().default(true),
    isDynamic: z.boolean().default(false),
    dynamicPageId: z.uuid().nullish(),
    translations: scalarTranslations.optional(),
  })
  // The hybrid pointer only means anything with a target (§2.1): a dynamic
  // static page with no dynamic_page_id renders an empty body.
  .refine((v) => !v.isDynamic || !!v.dynamicPageId, {
    message: 'isDynamic requires dynamicPageId',
    path: ['dynamicPageId'],
  });

export const updateStaticPageInput = z
  .object({
    title: z.string().trim().min(1).max(300),
    slug: entitySlug,
    content: z.string().max(200_000).nullable(),
    metaTitle: z.string().trim().max(300).nullable(),
    metaDescription: z.string().trim().max(1000).nullable(),
    metaKeywords: z.string().trim().max(500).nullable(),
    ogImageUrl: z.string().trim().max(512).nullable(),
    isActive: z.boolean(),
    isDynamic: z.boolean(),
    dynamicPageId: z.uuid().nullable(),
    translations: scalarTranslations,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

// ─── Menus ─────────────────────────────────────────────────────────────────

export const createMenuInput = z.object({
  name: z.string().trim().min(1).max(200),
  slug: entitySlug,
  isActive: z.boolean().default(true),
  translations: scalarTranslations.optional(),
});

export const updateMenuInput = z
  .object({
    name: z.string().trim().min(1).max(200),
    slug: entitySlug,
    isActive: z.boolean(),
    translations: scalarTranslations,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const createMenuItemInput = z.object({
  parentId: z.uuid().nullish(),
  label: z.string().trim().min(1).max(200),
  // Menu paths are navigation targets, not block CTAs — no {app} token, but
  // the same anti-`javascript:` allowlist applies.
  path: z
    .string()
    .trim()
    .min(1)
    .max(512)
    .regex(
      /^([#/]|https?:\/\/|mailto:|tel:)/,
      'must start with /, #, http://, https://, mailto: or tel:',
    ),
  icon: z.string().trim().max(80).nullish(),
  orderIndex: z.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().default(true),
  translations: scalarTranslations.optional(),
});

export const updateMenuItemInput = z
  .object({
    parentId: z.uuid().nullable(),
    label: z.string().trim().min(1).max(200),
    path: z
      .string()
      .trim()
      .min(1)
      .max(512)
      .regex(
        /^([#/]|https?:\/\/|mailto:|tel:)/,
        'must start with /, #, http://, https://, mailto: or tel:',
      ),
    icon: z.string().trim().max(80).nullable(),
    orderIndex: z.number().int().min(0).max(9999),
    isActive: z.boolean(),
    translations: scalarTranslations,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const reorderMenuItemsInput = z.object({
  order: z
    .array(z.object({ id: z.uuid(), orderIndex: z.number().int().min(0).max(9999) }))
    .min(1)
    .max(200),
});

// ─── Hero banners ──────────────────────────────────────────────────────────

export const createHeroBannerInput = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().max(2000).nullish(),
  buttonText: z.string().trim().max(200).nullish(),
  buttonLink: hrefRef.nullish(),
  imageUrl: z.string().trim().max(512).nullish(),
  isActive: z.boolean().default(true),
  orderIndex: z.number().int().min(0).max(9999).optional(),
  translations: scalarTranslations.optional(),
});

export const updateHeroBannerInput = z
  .object({
    title: z.string().trim().min(1).max(300),
    description: z.string().trim().max(2000).nullable(),
    buttonText: z.string().trim().max(200).nullable(),
    buttonLink: hrefRef.nullable(),
    imageUrl: z.string().trim().max(512).nullable(),
    isActive: z.boolean(),
    orderIndex: z.number().int().min(0).max(9999),
    translations: scalarTranslations,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const reorderHeroBannersInput = z.object({
  order: z
    .array(z.object({ id: z.uuid(), orderIndex: z.number().int().min(0).max(9999) }))
    .min(1)
    .max(200),
});

// ─── Website settings ──────────────────────────────────────────────────────

export const websiteSettingKey = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[a-z][a-z0-9_]*$/, 'lowercase snake_case key');

export const upsertWebsiteSettingInput = z.object({
  key: websiteSettingKey,
  // JSONB — a flag, a string, a nested object. NOT NULL in the schema, so a
  // literal `null` value is rejected here rather than 500ing in Postgres.
  value: z.unknown().refine((v) => v !== undefined && v !== null, {
    message: 'value is required and may not be null',
  }),
});

export const updateWebsiteSettingInput = z.object({
  value: z.unknown().refine((v) => v !== undefined && v !== null, {
    message: 'value is required and may not be null',
  }),
});
